"use client";

import type { Components } from "react-markdown";

/**
 * Shared markdown component configuration for react-markdown
 *
 * Security Note: react-markdown is safe by default and does not render raw HTML.
 * It only renders markdown syntax to React components, preventing XSS attacks.
 */
export const markdownComponents: Components = {
  h1: ({ node, ...props }) => (
    <h1
      className="text-2xl font-bold text-foreground mb-4 mt-6"
      {...props}
    />
  ),
  h2: ({ node, ...props }) => (
    <h2
      className="text-xl font-bold text-foreground mb-3 mt-5"
      {...props}
    />
  ),
  h3: ({ node, ...props }) => (
    <h3
      className="text-lg font-semibold text-foreground mb-2 mt-4"
      {...props}
    />
  ),
  p: ({ node, ...props }) => (
    <p
      className="text-foreground/80 leading-relaxed my-2"
      {...props}
    />
  ),
  a: ({ node, ...props }) => (
    <a
      className="text-primary underline hover:text-primary/80"
      {...props}
    />
  ),
  ul: ({ node, ...props }) => (
    <ul
      className="list-disc list-inside space-y-1 my-3 ml-4"
      {...props}
    />
  ),
  ol: ({ node, ...props }) => (
    <ol
      className="list-decimal list-inside space-y-1 my-3 ml-4"
      {...props}
    />
  ),
  li: ({ node, ...props }) => (
    <li className="leading-relaxed" {...props} />
  ),
  // Note: Using 'any' for node and spread props because react-markdown's Components type
  // doesn't provide precise types for these parameters. This is safe as we're just
  // spreading props to native HTML elements.
  code: ({ node, inline, ...props }: { node?: any; inline?: boolean; [key: string]: any }) =>
    inline ? (
      <code
        className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono"
        {...props}
      />
    ) : (
      <code className="text-sm font-mono" {...props} />
    ),
  pre: ({ node, ...props }) => (
    <pre
      className="bg-muted rounded p-3 my-3 overflow-x-auto"
      {...props}
    />
  ),
  blockquote: ({ node, ...props }) => (
    <blockquote
      className="border-l-4 border-border pl-4 italic text-muted-foreground my-3"
      {...props}
    />
  ),
  hr: ({ node, ...props }) => (
    <hr className="my-4 border-t border-border" {...props} />
  ),
};
